import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import {
  addArea,
  bulkInsertArea,
  deleteArea,
  fetchArea,
  updateArea,
} from "redux/actions/area";
import AreaRes from "types/res/area/AreaRes";
import { fetchShops } from "redux/actions/shop";
import { FormType } from "components/FormField";
import CreateAreaReq from "types/req/area/CreateAreaReq";
import UpdateAreaReq from "types/req/area/UpdateAreaReq";
import { Box, Button } from "@material-ui/core";
import CsvImport from "components/CsvImport";
import { CsvFieldType } from "utils/CsvUtils";

const Area: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const shops = useSelector((state) => state.shop);
  const area = useSelector((state) => state.area);
  const [openCsvImport, setOpenCsvImport] = useState(false);

  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchArea(companyId));
  }, [companyId, dispatch]);

  const headers: { key: keyof AreaRes; label: string }[] = [
    { key: "name", label: t("area.headers.name") },
    { key: "kana", label: t("area.headers.kana") },
    { key: "totalFee", label: t("area.headers.totalFee") },
    { key: "shopFee", label: t("area.headers.shopFee") },
    { key: "castFee", label: t("area.headers.castFee") },
    { key: "time", label: t("area.headers.time") },
    { key: "sort", label: t("area.headers.sort") },
    { key: "isGuestShow", label: t("area.headers.isGuestShow") },
    { key: "shops", label: t("area.headers.shops") },
  ];

  const forms = [
    {
      label: t("area.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("area.forms.kana"),
      key: "kana",
      type: FormType.Text,
    },
    {
      label: t("area.forms.totalFee"),
      key: "totalFee",
      type: FormType.Number,
    },
    {
      label: t("area.forms.shopFee"),
      key: "shopFee",
      type: FormType.Number,
    },
    {
      label: t("area.forms.castFee"),
      key: "castFee",
      type: FormType.Number,
    },
    {
      label: t("area.forms.time"),
      key: "time",
      type: FormType.Number,
    },
    {
      label: t("area.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
    {
      label: t("area.forms.isGuestShow"),
      key: "isGuestShow",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("area.options.hidden") },
        { id: "1", name: t("area.options.shown") },
      ],
    },
    {
      label: t("area.forms.shops"),
      key: "shops",
      type: FormType.MultiOptions,
      options: shops.map((s) => ({ id: Number(s.shopId), name: s.name })),
      value: (val: any) => val.map((s: any) => s.shopId),
    },
  ];

  const csvHeader = [
    {
      label: "名前",
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: "読みがな",
      key: "kana",
      type: CsvFieldType.Text,
    },
    {
      label: "料金※数字のみ",
      key: "totalFee",
      type: CsvFieldType.Number,
    },
    {
      label: "店舗料金※数字のみ",
      key: "shopFee",
      type: CsvFieldType.Number,
    },
    {
      label: "キャスト料金※数字のみ",
      key: "castFee",
      type: CsvFieldType.Number,
    },
    {
      label: "交通時間(分数)※数字のみ",
      key: "time",
      type: CsvFieldType.Number,
    },
    {
      label: "並び順",
      key: "sort",
      type: CsvFieldType.Number,
    },
    {
      label: "顧客モバイル表示",
      key: "isGuestShow",
      type: CsvFieldType.Boolean,
    },
    {
      label: "所属店舗",
      key: "shops",
      type: CsvFieldType.Array,
      relation: shops,
      relationKey: "name",
      relationId: "shopId",
    },
  ];

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        marginTop={1}
        marginRight={2}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCsvImport((prev) => !prev)}
          style={{ marginLeft: "30px" }}
        >
          {t("area.csvImport")}
        </Button>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/area.csv"}
          templateName={"area_template.csv"}
          csvHeader={csvHeader}
          addType={CreateAreaReq}
          addFunc={(formData) => bulkInsertArea(companyId, formData)}
        />
      )}
      <AreaTable
        title={t("area.title")}
        formId="areaForm"
        forms={forms}
        addFunc={(formData) => addArea(companyId, formData)}
        updateFunc={(formData) => updateArea(companyId, formData)}
        deleteFunc={(item) =>
          deleteArea(companyId, {
            areaId: item?.areaId,
          })
        }
        rows={headers}
        data={area}
        addType={CreateAreaReq}
        updateType={UpdateAreaReq}
        values={[
          (s) => s.name,
          (s) => s.kana,
          (s) => s.totalFee,
          (s) => s.shopFee,
          (s) => s.castFee,
          (s) => s.time,
          (s) => s.sort || t("area.values.unset"),
          (s) => (s.isGuestShow ? t("area.values.shown") : t("area.values.hidden")),
          (s) => s.shops?.map((shop) => shop.name).join(",") || t("area.values.notAssigned"),
        ]}
      />
    </>
  );
};

const AreaTable = styled<
  React.FC<CommonTableProps<AreaRes, CreateAreaReq, UpdateAreaReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Area;
